<template>
  <div
    class="is-card"
    @keydown.enter="searchOnEnter"
  >
    <ProposalQuickFilters
      :is-contract-management="isContractManagement"
      @paginate="paginate"
    />

    <div class="filter__grid">
      <!-- document and name -->
      <base-input
        :value="filters.multi_search"
        type="text"
        placeholder="Buscar CPF, Nome ou nº Proposta"
        @input="updateFilters({ multi_search: $event })"
        @blur="trimTextFilters"
      >
        Cliente
      </base-input>

      <!-- ccb -->
      <base-input
        v-if="showAllFilters && !isContractManagement"
        :value="filters.external_proposal_id"
        type="text"
        placeholder="Número da CCB"
        @input="updateFilters({ external_proposal_id: $event })"
        @blur="trimTextFilters"
      >
        CCB
      </base-input>

      <!-- contract number -->
      <base-input
        v-if="showAllFilters && isContractManagement"
        :value="filters.contract_number"
        type="text"
        placeholder="Número do contrato"
        @input="updateFilters({ contract_number: $event })"
        @blur="trimTextFilters"
      >
        N° do contrato
      </base-input>

      <!-- status -->
      <base-select
        v-if="!isCreditor && !isContractManagement"
        :value="filters.status"
        :options="statuses"
        :disabled="!statuses.length"
        can-filter
        @input="applyFilter({ status: $event })"
      >
        Status
      </base-select>

      <!-- Contract Management Status -->
      <base-select
        v-if="!isCreditor && isContractManagement && !isCollector"
        :value="filters.contract_status"
        :options="[
          { name: 'Pago', value: 'pago' },
          { name: 'Em atraso', value: 'em_atraso' },
          { name: 'Pago parcialmente', value: 'pago_parcialmente' },
          { name: 'Em dia', value: 'em_dia' },
        ]"
        can-filter
        @input="applyFilter({ contract_status: $event })"
      >
        Status
      </base-select>

      <!-- Assignee -->
      <base-select
        v-if="showAllFilters && isContractManagement"
        :value="filters.assignee"
        :options="assigneesOptions"
        :disabled="!assigneesOptions.length"
        can-filter
        @input="applyFilter({ assignee: $event })"
      >
        Cessionário
      </base-select>

      <!-- promoter -->
      <base-select
        v-if="showAllFilters && !isCreditor && !isContractManagement && !isProductActionCLT"
        :value="filters.promoter_id"
        :options="list.promoters"
        :searching="searching['promoter']"
        can-filter
        has-api-search
        @input="applyFilter({ promoter_id: $event })"
        @search="handlePromotersSearch($event, 'promoter')"
      >
        Promotor
      </base-select>

      <!-- responsible -->
      <base-select
        v-if="showAllFilters && !isCreditor && !isContractManagement && !isProductActionCLT"
        :value="filters.responsible_id"
        :options="list.responsible"
        :searching="searching['responsible']"
        can-filter
        has-api-search
        @input="applyFilter({ responsible_id: $event })"
        @search="handleResponsibleSearch(
          $event,
          { ...(promoter_id ? { promoter_id } : '') },
          'responsible',
        )"
      >
        Responsável
      </base-select>

      <!-- Priority -->
      <base-select
        v-if="showAllFilters && isContractManagement && !isCollector"
        :value="filters.priority_level"
        :options="[
          { name: 'Urgente', value: 'urgente' },
          { name: 'Alta', value: 'alta' },
          { name: 'Media', value: 'media' },
          { name: 'Baixa', value: 'baixa' },
        ]"
        can-filter
        @input="applyFilter({ priority_level: $event })"
      >
        Prioridade
      </base-select>

      <!-- bank -->
      <base-select
        v-if="showAllFilters && !isProductCGV && !isContractManagement"
        :value="filters.supplier_id"
        :options="suppliers"
        :disabled="!suppliers.length"
        can-filter
        @input="applyFilter({ supplier_id: $event })"
      >
        <span>Banco</span>
      </base-select>

      <!-- Week Day -->
      <base-select
        v-if="showAllFilters && isContractManagement && !isCollector"
        :value="filters.week_day"
        :options="WEEK_DAYS"
        can-filter
        @input="applyFilter({ week_day: $event })"
      >
        Dias da semana
        <InfoIcon v-tooltip="'Filtra todos os contratos pagos no dia da semana selecionado.'" />
      </base-select>

      <!-- issue_type -->
      <base-select
        v-if="showAllFilters && !isContractManagement"
        :value="filters.issue_type"
        :options="ISSUE_TYPE_OPTIONS"
        can-filter
        @input="applyFilter({ issue_type: $event })"
      >
        Tipos de pendência
      </base-select>

      <!-- period -->
      <base-calendar
        v-model="form.period"
        class="period"
        @input="setPeriod"
      >
        Período
      </base-calendar>
    </div>

    <div
      :class="{ active: showAllFilters }"
      class="filter__switch"
    >
      <!-- antifraud -->
      <base-switch
        v-if="
          showAllFilters
            && !isProductCGV
            && !isContractManagement
        "
        :value="filters.antifraud_completed"
        @input="applyFilter({ antifraud_completed: $event })"
      >
        <span>Formalizados</span>
      </base-switch>

      <!-- ccb -->
      <base-switch
        v-if="
          showAllFilters
            && !isProductCGV
            && !isContractManagement
        "
        :value="filters.external_proposal_id_not_null"
        @input="applyFilter({ external_proposal_id_not_null: $event })"
      >
        <span>CCB Gerada</span>
      </base-switch>

      <!-- self contracting -->
      <base-switch
        v-if="
          showAllFilters
            && hasSelfContractingLink
            && !isContractManagement
        "
        :value="filters.self_contracting"
        @input="applyFilter({ self_contracting: $event })"
      >
        <span>Auto-contratação</span>
        <InfoIcon
          v-tooltip="'Propostas geradas através do link de autocontratação.'"
          class="ml-2"
        />
      </base-switch>

      <!-- with shutdown (apenas afastados) -->
      <base-switch
        v-if="
          showAllFilters
            && isContractManagement
            && !isCollector
        "
        :value="filters.with_shutdown"
        @input="applyFilter({ with_shutdown: $event })"
      >
        <span>Apenas afastados</span>
        <InfoIcon
          v-tooltip="'Listas apenas clientes afastados ou desligados.'"
          class="ml-2"
        />
      </base-switch>
    </div>

    <div class="filter__actions">
      <btn-show-all v-model="showAllFilters" />
      <button
        class="button"
        @click="clean"
      >
        {{ isMobile ? 'Limpar' : 'Limpar todos os campos' }}
      </button>
      <button
        ref="searchButton"
        class="button is-success filter__search"
        @click="search"
      >
        Buscar
        <kbd
          v-if="!isMobile"
          class="filter__key"
          aria-hidden="true"
        >Enter</kbd>
      </button>
    </div>

    <ListingSearchCancel @withdrawn="focusSearchWhen(true)" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { handler as handlerError } from '@/utils/message';

import config from '@/data/config';
import { WEEK_DAYS } from '@/data/week_days';
import { ISSUE_TYPE_OPTIONS, buildTrimmedTextFiltersPatch } from '@/utils/workerCreditContractManagement';

import promoterPaginationMixin from '@/mixins/promoterPagination.mixin';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import loadingMixin from '@/mixins/loadingView.mixin';

import BaseInput from '@/components/Base/Input/index.vue';
import BaseSelect from '@/components/Base/Select/index.vue';
import BaseCalendar from '@/components/BaseCalendar.vue';
import BtnShowAll from '@/components/Base/BtnShowAll/index.vue';
import BaseSwitch from '@/components/Base/Switch/index.vue';
import InfoIcon from '@/assets/icons/Info.vue';
import ProposalQuickFilters from '@/components/ProposalQuickFilters.vue';
import ListingSearchCancel from '@/components/ListingSearchCancel.vue';
import { LISTING_SEARCH, isEnterFromControl } from '@/composables/useListingSearch';

export default {
  name: 'ProposalFilters',
  components: {
    BaseInput,
    BaseSelect,
    BaseCalendar,
    BtnShowAll,
    BaseSwitch,
    InfoIcon,
    ProposalQuickFilters,
    ListingSearchCancel,
  },
  mixins: [
    promoterPaginationMixin,
    roleManagerMixin,
    loadingMixin,
  ],
  inject: {
    listingSearch: { from: LISTING_SEARCH, default: null },
  },
  props: {
    productId: {
      type: [String, Number],
      required: true,
    },
    isContractManagement: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    storageKey: 'filters',
    WEEK_DAYS,
    ISSUE_TYPE_OPTIONS,
    form: {
      period: [],
    },
    list: {
      responsible: [],
      promoters: [],
    },
    toExclude: [
      'begin_date',
      'end_date',
    ],
    showAllFilters: false,
  }),
  computed: {
    ...mapGetters({
      isMobile: 'app/isMobile',
      assigneesOptions: 'contract/assigneesOptions',
      isInternalUser: 'user/isInternalUser',
    }),
    ...mapState({
      statuses: (state) => state.app.statuses,
      suppliers: (state) => state.app.suppliers,
      $_filters: (state) => state.proposal.filters,
    }),
    promoter_id() {
      return this.filters.promoter_id;
    },
    cnpjOptions() {
      return [
        { name: 'PB', value: '61820817000109' },
        { name: 'Prata', value: '62271128000147' },
      ];
    },
    filters() {
      return this.$_filters;
    },
    hasFilters() {
      return Object
        .values(this.filters)
        .some((val) => Boolean(val));
    },

    // visivility
    canViewFilterCNPJ() {
      return [
        this.isSuper,
        this.isAntifraud,
        this.isCompliance,
      ].some((isValid) => isValid);
    },

    // product
    isProductINSS() {
      return config.PRODUCT_ID_INSS === this.productId;
    },
    isProductCLT() {
      return config.PRODUCT_ID_WORKER_CREDIT === this.productId;
    },
    isProductCGV() {
      return config.PRODUCT_ID_CGV === this.productId;
    },
    isProductActionCLT() {
      return config.PRODUCT_ID_WORKER_CREDIT_ACTION === this.productId;
    },
    hasSelfContractingLink() {
      return [
        config.PRODUCT_ID_FGTS,
        config.PRODUCT_ID_CGV,
        config.PRODUCT_ID_CREDIT_CARD,
        config.PRODUCT_ID_WORKER_CREDIT,
      ].includes(this.productId);
    },
  },
  watch: {
    'filters.begin_date': {
      handler() {
        this.syncPeriod();
      },
    },
    'filters.end_date': {
      handler() {
        this.syncPeriod();
      },
    },
    $_filters: {
      deep: true,
      handler() {
        this.setFilterSearched(false);
      },
    },
    'listingSearch.awaitingSearch': 'focusSearchWhen',
    'listingSearch.cancelled': 'focusSearchWhen',
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    ...mapActions({
      updateFilters: 'proposal/UPDATE_FILTERS',
      setFilterSearched: 'proposal/SET_FILTER_SEARCHED',
      resetFilters: 'proposal/RESET_FILTERS',
      getAssignees: 'contract/GET_ASSIGNEES',
    }),

    // handlers
    handlerError,
    async loadData() {
      this.getAssignees();

      if (this.filters.begin_date && this.filters.end_date) {
        this.syncPeriod();
      }

      if (this.hasFilters) {
        this.setFilterSearched(true);
      }
    },
    syncPeriod() {
      this.form.period = [
        this.filters.begin_date,
        this.filters.end_date,
      ];
    },
    setPeriod([start, end]) {
      this.applyFilter({ begin_date: start, end_date: end });
    },
    // Discrete fields (select/switch/period) search immediately on change,
    // matching the chip's own "x" in ProposalFiltersBy — text inputs stay on
    // updateFilters only, so typing doesn't fire a request per keystroke.
    applyFilter(patch) {
      this.updateFilters(patch);
      this.paginate();
    },
    reset() {
      this.resetFilters();
      this.setFilterSearched(true);
    },
    paginate() {
      sessionStorage.removeItem(this.storageKey);
      this.$emit('paginate');

      // after $_filters watcher, reset filterSearched at each filter change
      this.$nextTick(() => this.setFilterSearched(true));
    },

    trimTextFilters() {
      const patch = buildTrimmedTextFiltersPatch(this.filters);

      if (!Object.keys(patch).length) return;

      this.updateFilters(patch);
    },

    // actions
    clean() {
      this.reset();
      this.paginate();
    },
    search() {
      this.trimTextFilters();
      this.paginate();
    },
    searchOnEnter(event) {
      if (isEnterFromControl(event)) return;
      this.search();
    },
    focusSearchWhen(isWaiting) {
      if (isWaiting) this.$nextTick(() => this.$refs.searchButton?.focus());
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_buttonsVariables.scss';
@import '@/assets/scss/src/_textVariables.scss';

.filter {
  &__grid {
    display: grid;
    gap: 24px;

    > div {
      margin: 0;
    }
  }

  &__switch {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &.active {
      margin-top: 24px;
    }

    > div {
      margin: 0;
    }
  }

  &__actions {
    margin-top: 24px;
    display: grid;

    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
    gap: 1rem;

    > div {
      grid-column: span 2;
    }

    > button {
      width: 100%;
    }

    :deep(.divider-expand) {
      margin-bottom: 16px;
    }
  }

  &__search {
    gap: .5rem;

    &:hover,
    &:focus {
      background-color: $prata-green-700;
    }

    &:focus .filter__key {
      display: inline-block;
    }
  }

  &__key {
    display: none;
    padding: 0 .375rem;

    border: 1px solid currentColor;
    border-radius: $radius-control;

    font: {
      family: $font-family;
      size: 10px;
    }
    line-height: 1.125rem;

    pointer-events: none;
  }
}

@media only screen and (min-width: 1024px) {
  .filter {
    &__grid {
      grid-template-columns: repeat(6, 1fr);
      align-items: end;

      > div {
        grid-column: span 2;

        // span 4
        &:nth-child(2):last-child,
        &:nth-child(5):last-child,
        &:nth-child(8):last-child {
          grid-column: span 4;
        }

        // span 6
        &:nth-child(10):last-child {
          grid-column: span 6;
        }
      }
    }

    &__switch {
      flex-direction: row;
    }

    &__actions {
      grid-template-columns: 1fr auto auto;
      grid-template-rows: 1fr;

      > div {
        grid-column: 1;
      }

      > button {
        width: fit-content;
      }

      :deep(.divider-expand) {
        margin-bottom: 0;
      }
    }
  }
}
</style>
